import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import { ArrowRight, Crown, TrendingUp, Users, PhoneCall, Check, Link2, Printer } from "lucide-react";
import { LaurelMark } from "@/components/Logo";
import { AREAS, areaByName } from "@/data/auditData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMMUNITY_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_victory-audit/artifacts/vep3jazu_907D1D4A-799D-4142-8B41-8FDDC9C2BFFD.png";
const CALL_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_victory-audit/artifacts/bs5vio0f_882622B3-2FEE-4B79-B6B8-37BA9ED06D91.PNG";

const SUPPORT_OPTIONS = [
  { id: "community", label: "Lady of Victory® Community", desc: "Grow alongside women leading themselves with intention.", icon: Users, img: COMMUNITY_IMG },
  { id: "connection_call", label: "Victory Connection Call", desc: "A private 1:1 conversation about your results and next step.", icon: PhoneCall, img: CALL_IMG },
  { id: "not_now", label: "Not right now", desc: "Receive my results and explore on my own for now.", icon: null, img: null },
];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Results() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [support, setSupport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("lov_audit_results");
    if (!raw) {
      navigate("/audit", { replace: true });
      return;
    }
    setData(JSON.parse(raw));
  }, [navigate]);

  if (!data) return null;
  const { scores, strongest, gap, overall } = data.results;
  const strongestArea = areaByName(strongest);
  const gapArea = areaByName(gap);
  const chartData = AREAS.map((a) => ({ area: a.area, score: scores[a.area] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !support) {
      toast.error("Please add your name, email, and choose your next step.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        overall_result: overall,
        strongest_area: strongest,
        primary_gap: gap,
        wants_support: SUPPORT_OPTIONS.find((o) => o.id === support).label,
        scores,
        referred_by: sessionStorage.getItem("lov_ref") || null,
      });
      setLeadId(res.data.id);
      setSubmitted(true);
      toast.success("Your results are on their way to you.");
    } catch (err) {
      toast.error("Something went wrong saving your results. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="results-page">
      <header className="border-b border-burgundy/10 bg-ivory/90 backdrop-blur-md sticky top-0 z-40 no-print">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="flex items-center gap-3">
            <LaurelMark className="w-6 h-6" />
            <span className="font-serif text-lg text-burgundy">Your Results</span>
          </span>
          <div className="flex items-center gap-5">
            <button
              data-testid="results-print-btn"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-burgundy/60 hover:text-burgundy transition-colors"
            >
              <Printer className="w-4 h-4" /> Keepsake
            </button>
            <button
              data-testid="results-retake-btn"
              onClick={() => navigate("/audit")}
              className="text-xs uppercase tracking-[0.25em] text-burgundy/60 hover:text-burgundy transition-colors"
            >
              Retake
            </button>
          </div>
        </div>
      </header>

      <div className="print-only px-6 pt-10 text-center">
        <p className="text-xs uppercase tracking-[0.35em] text-[#A08C6E] mb-2">Lady of Victory® Alignment Check</p>
        <h2 className="font-serif text-3xl text-[#0F2036]">
          {name ? `${name} — ` : ""}{new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </h2>
      </div>

      <section className="bg-burgundy-deep text-champagne py-20 text-center relative overflow-hidden">
        <motion.p {...reveal(0)} className="text-xs uppercase tracking-[0.35em] text-gold mb-6">
          Your responsibilities aren't your identity.
        </motion.p>
        <motion.h1 {...reveal(0.1)} className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="results-overall-score">
          {overall}<span className="text-gold text-3xl align-top">/100</span>
        </motion.h1>
        <motion.p {...reveal(0.2)} className="mt-4 text-champagne/70 max-w-xl mx-auto text-base sm:text-lg">
          Your overall self-leadership score — a mirror of how intentionally you are
          leading the woman behind the roles.
        </motion.p>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div {...reveal(0)} data-testid="results-radar-chart" className="h-80 sm:h-96">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} outerRadius="72%">
              <PolarGrid stroke="rgba(31,58,95,0.15)" />
              <PolarAngleAxis dataKey="area" tick={{ fill: "#1F3A5F", fontSize: 12, fontFamily: "Plus Jakarta Sans" }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar dataKey="score" stroke="#1F3A5F" fill="#1F3A5F" fillOpacity={0.25} strokeWidth={2} isAnimationActive />
            </RadarChart>
          </ResponsiveContainer>
        </motion.div>

        <div className="grid gap-6">
          <motion.div {...reveal(0.1)} className="border border-gold/50 bg-[#F1EFE7] p-8" data-testid="strongest-area-card">
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold-muted mb-4">
              <Crown className="w-4 h-4" /> Your strongest area
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl text-burgundy-deep" data-testid="strongest-area-name">
              {strongest} · {scores[strongest]}%
            </h2>
            <p className="mt-3 text-[#62686E] leading-relaxed">{strongestArea.affirmation}</p>
          </motion.div>

          <motion.div {...reveal(0.2)} className="border border-burgundy/25 bg-[#EEF0F2] p-8" data-testid="growth-gap-card">
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-burgundy-light mb-4">
              <TrendingUp className="w-4 h-4" /> Your growth area
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl text-burgundy-deep" data-testid="growth-area-name">
              {gap} · {scores[gap]}%
            </h2>
            <p className="mt-3 text-[#62686E] leading-relaxed">{gapArea.gapExplanation}</p>
            <div className="mt-5 border-l-2 border-gold pl-4">
              <p className="text-xs uppercase tracking-[0.25em] text-gold-muted mb-1">One intentional action</p>
              <p className="text-burgundy-deep font-medium leading-relaxed" data-testid="intentional-action-text">
                {gapArea.intentionalAction}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-burgundy-deep text-champagne py-24 no-print">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div {...reveal(0)} className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.35em] text-gold mb-6">Ready to close the gap?</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight">
              Your check showed you where you are.
            </h2>
            <p className="mt-4 text-champagne/70 leading-relaxed">
              The next step is learning how to intentionally lead yourself from identity,
              not from the demands of your roles. Choose where you'd like to continue.
            </p>
          </motion.div>

          <div className="mt-14 grid sm:grid-cols-2 gap-6">
            {SUPPORT_OPTIONS.filter((o) => o.img).map((o, i) => (
              <motion.button
                key={o.id}
                {...reveal(0.1 + i * 0.1)}
                data-testid={`support-option-${o.id}`}
                onClick={() => setSupport(o.id)}
                className={`text-left border transition-colors duration-300 overflow-hidden group ${
                  support === o.id ? "border-gold bg-champagne/10" : "border-champagne/20 hover:border-gold/50"
                }`}
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={o.img}
                    alt={o.label}
                    className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${
                      o.id === "community" ? "object-contain bg-[#F8F4EC] p-4" : "object-contain bg-white"
                    }`}
                  />
                </div>
                <div className="p-6 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-xl text-champagne">{o.label}</h3>
                    <p className="mt-2 text-sm text-champagne/60 leading-relaxed">{o.desc}</p>
                  </div>
                  <span className={`mt-1 w-6 h-6 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                    support === o.id ? "border-gold bg-gold text-burgundy-deep" : "border-champagne/40"
                  }`}>
                    {support === o.id && <Check className="w-3.5 h-3.5" />}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
          <motion.div {...reveal(0.25)} className="mt-4 text-center">
            <button
              data-testid="support-option-not-now"
              onClick={() => setSupport("not_now")}
              className={`text-xs uppercase tracking-[0.25em] transition-colors ${
                support === "not_now" ? "text-gold" : "text-champagne/50 hover:text-champagne"
              }`}
            >
              Not right now — just send my results
            </button>
          </motion.div>

          {!submitted ? (
            <motion.form {...reveal(0.3)} onSubmit={submit} className="mt-12 max-w-xl mx-auto grid gap-4" data-testid="lead-capture-form">
              <input
                data-testid="lead-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="bg-transparent border border-champagne/25 px-5 py-4 text-champagne placeholder:text-champagne/40 focus:outline-none focus:border-gold transition-colors"
              />
              <input
                data-testid="lead-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="bg-transparent border border-champagne/25 px-5 py-4 text-champagne placeholder:text-champagne/40 focus:outline-none focus:border-gold transition-colors"
              />
              <button
                data-testid="lead-submit-btn"
                type="submit"
                disabled={submitting}
                className="group mt-2 inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-burgundy-deep text-sm font-semibold tracking-widest uppercase hover:bg-champagne transition-colors disabled:opacity-60"
              >
                {submitting ? "Saving…" : "Save My Results & Next Step"}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </motion.form>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 max-w-xl mx-auto text-center border border-gold/50 bg-champagne/10 p-10"
              data-testid="lead-success-message"
            >
              <LaurelMark className="w-8 h-8" chip />
              <h3 className="font-serif text-2xl text-champagne">Well done, {name.split(" ")[0]}.</h3>
              <p className="mt-3 text-champagne/70 leading-relaxed">
                {support === "community" &&
                  "You've chosen the Lady of Victory® Community. Take the final step now — join the circle of women leading from identity."}
                {support === "connection_call" &&
                  "You've chosen a Victory Connection Call. Take the final step now — reserve your private conversation below."}
                {support === "not_now" &&
                  "Your results are saved. When you're ready to close the gap, the Community and a Victory Connection Call will be waiting."}
              </p>
              {support === "community" && (
                <a
                  data-testid="join-community-link"
                  href="https://sharis-robinson.mycoursecreator360.com/payment-link/6a9b5a63a7f78e147447f080"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-6 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-3.5 text-burgundy-deep text-sm font-semibold tracking-widest uppercase hover:bg-champagne transition-colors"
                >
                  Join the Community
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              )}
              {support === "connection_call" && (
                <a
                  data-testid="book-connection-call-link"
                  href="https://victoriousleadingsolutions.com/connection-call-903959"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-6 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-3.5 text-burgundy-deep text-sm font-semibold tracking-widest uppercase hover:bg-champagne transition-colors"
                >
                  Book Your Connection Call
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              )}
              {leadId && (
                <div className="mt-8 border-t border-champagne/15 pt-6" data-testid="share-results-box">
                  <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">
                    Know a woman carrying too much? Share your results.
                  </p>
                  <button
                    data-testid="copy-share-link-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/r/${leadId}`);
                      setCopied(true);
                      toast.success("Your results link is copied — pass it on.");
                      setTimeout(() => setCopied(false), 2500);
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-champagne/30 px-6 py-2.5 text-xs tracking-widest uppercase text-champagne hover:border-gold hover:text-gold transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                    {copied ? "Copied" : "Copy My Results Link"}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      <footer className="bg-burgundy-deep border-t border-gold/15 py-8 text-center no-print">
        <p className="text-xs text-champagne/40 tracking-wide">
          © {new Date().getFullYear()} Lady of Victory® · Your responsibilities aren't your identity.
        </p>
      </footer>
    </div>
  );
}
